import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  House, BookOpen, Heart, Target, ListChecks, Sun, Flame, SignOut, Calendar, HandHeart, Leaf, Pulse,
} from "@phosphor-icons/react";

const BASE_NAV = [
  { to: "/app", label: "Dashboard", icon: House, end: true },
  { to: "/app/journal", label: "Journal", icon: BookOpen },
  { to: "/app/mood", label: "Mood", icon: Heart },
  { to: "/app/goals", label: "Goals", icon: Target },
  { to: "/app/todos", label: "Tasks", icon: ListChecks },
  { to: "/app/gratitude", label: "Gratitude", icon: Sun },
  { to: "/app/habits", label: "Habits", icon: Flame },
];

const PROGRAM_NAV = {
  grief180: { to: "/app/grief", label: "Grief", icon: HandHeart },
  sober30: { to: "/app/sober", label: "Sober Living", icon: Leaf },
  chronic365: { to: "/app/chronic", label: "Living Well", icon: Pulse },
};

export default function Layout({ children }) {
  const { user, access, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => { await logout(); navigate("/"); };

  const programs = user?.programs || [];
  // Show program nav if owned OR if chronic trial is active
  const chronicTrialActive = !programs.includes("chronic365") && user?.chronic_trial_end && new Date(user.chronic_trial_end) > new Date();
  const visibleProgramKeys = [...programs.filter((p) => PROGRAM_NAV[p])];
  if (chronicTrialActive) visibleProgramKeys.push("chronic365");
  const NAV = [
    ...BASE_NAV,
    ...visibleProgramKeys.map((p) => PROGRAM_NAV[p]).filter(Boolean),
    { to: "/app/book", label: "Book Angela", icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-sand text-charcoal">
      {/* Sticky glass top bar */}
      <header className="sticky top-0 z-30 glass border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-logo">
            <span className="w-2.5 h-2.5 rounded-full bg-moss"></span>
            <span className="font-serif text-xl md:text-2xl tracking-tight leading-tight">Rediscover Yourself <span className="text-clay text-sm md:text-base block md:inline">· with Angela Gehl</span></span>
          </Link>
          <div className="flex items-center gap-4">
            {access && (
              <div className="text-xs text-muted-foreground hidden md:block" data-testid="access-pill">
                {access.is_paid ? (
                  <span className="px-3 py-1 rounded-full bg-moss/10 text-moss font-medium tracking-wide">Lifetime member</span>
                ) : access.is_trial ? (
                  <Link to="/app/pricing" className="px-3 py-1 rounded-full bg-clay/10 text-clay font-medium tracking-wide hover:bg-clay/20 transition-colors">
                    Trial · {access.days_left} day{access.days_left === 1 ? "" : "s"} left
                  </Link>
                ) : (
                  <Link to="/app/pricing" className="px-3 py-1 rounded-full bg-destructive/10 text-destructive font-medium">
                    Trial ended — Unlock
                  </Link>
                )}
              </div>
            )}
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
            <button onClick={handleLogout} data-testid="logout-button"
                    className="text-muted-foreground hover:text-charcoal transition-colors">
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        {/* Side nav */}
        <nav className="lg:sticky lg:top-24 self-start">
          <ul className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {NAV.map((n) => (
              <li key={n.to} className="shrink-0">
                <NavLink to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm transition-all ${
                      isActive
                        ? "bg-moss text-white"
                        : "text-charcoal/70 hover:bg-cream hover:text-charcoal"
                    }`
                  }>
                  <n.icon size={18} weight="duotone" />
                  <span>{n.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main key={location.pathname} className="animate-fade-up min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
